import type { ReactNode } from 'react';

function isAllowedHref(href: string): boolean {
  return /^\/(admin|candidate|invigilator)(\/|$)/i.test(href.trim());
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let index = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={`${keyPrefix}-b-${index}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      nodes.push(<em key={`${keyPrefix}-i-${index}`}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`')) {
      nodes.push(<code key={`${keyPrefix}-c-${index}`}>{token.slice(1, -1)}</code>);
    } else {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link && isAllowedHref(link[2])) {
        nodes.push(
          <a key={`${keyPrefix}-a-${index}`} href={link[2]} className="text-primary-700 underline dark:text-primary-300">
            {link[1]}
          </a>
        );
      } else {
        nodes.push(token);
      }
    }
    lastIndex = match.index + token.length;
    index += 1;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

export function SafeGuideMarkdown({ text }: { text: string }) {
  const lines = (text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let listItems: string[] = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(
      <ul key={`ul-${key++}`} className="list-disc space-y-1 pl-5">
        {listItems.map((item, itemIndex) => (
          <li key={`li-${key}-${itemIndex}`}>{renderInline(item, `li-${key}-${itemIndex}`)}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flushList();
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      listItems.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }
    flushList();
    if (line.startsWith('### ')) {
      blocks.push(<h4 key={`h4-${key++}`} className="text-sm font-semibold theme-ink">{renderInline(line.slice(4), `h4-${key}`)}</h4>);
    } else if (line.startsWith('## ')) {
      blocks.push(<h3 key={`h3-${key++}`} className="text-base font-semibold theme-ink">{renderInline(line.slice(3), `h3-${key}`)}</h3>);
    } else if (line.startsWith('# ')) {
      blocks.push(<h2 key={`h2-${key++}`} className="text-lg font-semibold theme-ink">{renderInline(line.slice(2), `h2-${key}`)}</h2>);
    } else {
      blocks.push(<p key={`p-${key++}`} className="text-sm leading-relaxed theme-ink">{renderInline(line, `p-${key}`)}</p>);
    }
  }
  flushList();

  if (blocks.length === 0) {
    return <p className="text-sm theme-muted">{text || ''}</p>;
  }

  return <div className="space-y-2">{blocks}</div>;
}
